import { useEffect, useState } from 'react'

import { Input, StyledText } from '@skytech/ui'
import { Container } from '@skytech/ui/Container'
import { isNil, useDebounce } from '@skytech/utils'
import { useQuery } from '@tanstack/react-query'

import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import { FormattedNumber } from '@/components/FormattedNumber'
import Tabs from '@/components/Tabs'
import { useSearchParamsByKey } from '@/hooks/searchParams'
import { ECOM_ORDER_STATUS } from '@/modules/ecom-policy/constants'

import queries from '../queries'
import { ecomPolicyResources } from '../resources'
import type { PolicyOrder } from '../types'

import EcomPolicyOrderList from './EcomPolicyOrderList'

import './index.styles.scss'

export const getStatusCounter = (orders: PolicyOrder[]) => {
  const newStatusCategory = [
    ECOM_ORDER_STATUS.NEW,
    ECOM_ORDER_STATUS.ACCEPTED,
    ECOM_ORDER_STATUS.CONFIRMED,
  ]
  return orders.reduce(
    (acc, cur) => {
      acc[ECOM_ORDER_STATUS.ALL]++

      if (newStatusCategory.includes(cur.statusId)) {
        acc[ECOM_ORDER_STATUS.NEW] += 1
        return acc
      }

      acc[cur.statusId] = acc[cur.statusId] += 1

      return acc
    },
    {
      [ECOM_ORDER_STATUS.ALL]: 0,
      [ECOM_ORDER_STATUS.AWAITING_APPROVAL]: 0,
      [ECOM_ORDER_STATUS.PENDING_DEALER_APPROVAL]: 0,
      [ECOM_ORDER_STATUS.NEW]: 0,
      [ECOM_ORDER_STATUS.REJECTED]: 0,
      [ECOM_ORDER_STATUS.CANCELED]: 0,
      [ECOM_ORDER_STATUS.DELIVERED]: 0,
    }
  )
}

const EcomPolicyOrderListPage = () => {
  const translate = useTranslation()

  const [queryStatus, setQueryStatus] = useSearchParamsByKey('status')
  const [querySearch, setQuerySearch] = useSearchParamsByKey('search')

  const defaultStatusId = isNil(queryStatus)
    ? ECOM_ORDER_STATUS.AWAITING_APPROVAL
    : Number(queryStatus)
  const [searchFilter, setSearchFilter] = useState(querySearch || '')
  const [statusFilter, setStatusFilter] = useState(defaultStatusId)

  const debouncedSearchTerm = useDebounce(searchFilter, 500)
  useEffect(() => {
    setSearchFilter(querySearch || '')
    setStatusFilter(defaultStatusId)
  }, [defaultStatusId, querySearch, queryStatus])

  useEffect(() => {
    setQuerySearch(debouncedSearchTerm)
  }, [debouncedSearchTerm, setQuerySearch])

  const { data: orderList = [] } = useQuery(
    queries.policyOrderList(),
    ecomPolicyResources.getOrderList
  )
  const statusCounter = getStatusCounter(orderList)
  const statusFilterOptions = [
    {
      value: ECOM_ORDER_STATUS.ALL,
      label: (
        <span>
          <Translation id="ecom_order_status/ALL" />
          <StyledText
            size="regular-plus"
            colorMode={statusCounter[ECOM_ORDER_STATUS.ALL] === 0 ? 'gray' : undefined}
            className="count-styling"
          >
            <FormattedNumber
              className="ml-2 font-bold"
              amount={statusCounter[ECOM_ORDER_STATUS.ALL]}
            />
          </StyledText>
        </span>
      ),
    },
    {
      value: ECOM_ORDER_STATUS.AWAITING_APPROVAL,
      label: (
        <span>
          <Translation id="ecom_order_status/AWAITING_APPROVAL" />
          <StyledText
            size="regular-plus"
            colorMode={statusCounter[ECOM_ORDER_STATUS.ALL] === 0 ? 'gray' : undefined}
            className="count-styling"
          >
            <FormattedNumber
              className="ml-2 font-bold"
              amount={statusCounter[ECOM_ORDER_STATUS.AWAITING_APPROVAL]}
            />
          </StyledText>
        </span>
      ),
    },
    {
      value: ECOM_ORDER_STATUS.PENDING_DEALER_APPROVAL,
      label: (
        <span>
          <Translation id="ecom_order_status/PENDING_DEALER_APPROVAL" />
          <StyledText
            size="regular-plus"
            colorMode={statusCounter[ECOM_ORDER_STATUS.ALL] === 0 ? 'gray' : undefined}
            className="count-styling"
          >
            <FormattedNumber
              className="ml-2 font-bold"
              amount={statusCounter[ECOM_ORDER_STATUS.PENDING_DEALER_APPROVAL]}
            />
          </StyledText>
        </span>
      ),
    },

    {
      value: ECOM_ORDER_STATUS.NEW,
      label: (
        <span>
          <Translation id="ecom_order_status/APPROVED" />
          <StyledText
            size="regular-plus"
            colorMode={statusCounter[ECOM_ORDER_STATUS.ALL] === 0 ? 'gray' : undefined}
            className="count-styling"
          >
            <FormattedNumber
              className="ml-2 font-bold"
              amount={statusCounter[ECOM_ORDER_STATUS.NEW]}
            />
          </StyledText>
        </span>
      ),
    },
    {
      value: ECOM_ORDER_STATUS.REJECTED,
      label: (
        <span>
          <Translation id="ecom_order_status/REJECTED" />
          <StyledText
            size="regular-plus"
            colorMode={statusCounter[ECOM_ORDER_STATUS.ALL] === 0 ? 'gray' : undefined}
            className="count-styling"
          >
            <FormattedNumber
              className="ml-2 font-bold"
              amount={statusCounter[ECOM_ORDER_STATUS.REJECTED]}
            />
          </StyledText>
        </span>
      ),
    },
    {
      value: ECOM_ORDER_STATUS.CANCELED,
      label: (
        <span>
          <Translation id="ecom_order_status/CANCELLED" />
          <StyledText
            size="regular-plus"
            colorMode={statusCounter[ECOM_ORDER_STATUS.ALL] === 0 ? 'gray' : undefined}
            className="count-styling"
          >
            <FormattedNumber
              className="ml-2 font-bold"
              amount={statusCounter[ECOM_ORDER_STATUS.CANCELED]}
            />
          </StyledText>
        </span>
      ),
    },
    {
      value: ECOM_ORDER_STATUS.DELIVERED,
      label: (
        <span>
          <Translation id="ecom_order_status/DELIVERED" />
          <StyledText
            size="regular-plus"
            colorMode={statusCounter[ECOM_ORDER_STATUS.ALL] === 0 ? 'gray' : undefined}
            className="count-styling"
          >
            <FormattedNumber
              className="ml-2 font-bold"
              amount={statusCounter[ECOM_ORDER_STATUS.DELIVERED]}
            />
          </StyledText>
        </span>
      ),
    },
  ]

  return (
    <Container className="ecom-policy-order-list">
      <Container.Header>
        <Translation id="Shop Orders" />
      </Container.Header>

      <div className="device-application-list__options">
        <div className="device-application-list__options__option">
          <Tabs
            tabs={statusFilterOptions}
            selectedValue={statusFilter}
            onClick={statusId => {
              setStatusFilter(Number(statusId))
              setQueryStatus(statusId)
            }}
          />
        </div>

        <div className="device-application-list__options__option">
          <Input
            id="searchFilter"
            type="text"
            value={searchFilter}
            placeholder={translate('Search')}
            onChange={e => setSearchFilter(e.target.value)}
          />
        </div>
      </div>

      <EcomPolicyOrderList searchFilter={searchFilter} statusFilter={statusFilter} />
    </Container>
  )
}

export default EcomPolicyOrderListPage
